import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock3 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { usePosts } from "../hooks/usePosts";
import { SITE } from "../data/site";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const Blog = () => {
  const { data: posts } = usePosts();

  return (
    <>
      <Seo
        title="Blog | Cable Jointing & 3M Accessories Insights — Universal Engineers Delhi"
        description="Technical guides on 3M cable jointing kits, terminations, tapes and their role in solar, wind, metro and infrastructure projects — from Universal Engineers, 3M channel partner since 1972."
        keywords="3M cable accessories blog, cable jointing guide India, heat shrink vs cold shrink, cable termination technical guide, solar cable accessories India, 33kV jointing kit selection"
        path="/blog"
      />

      <section className="grain relative overflow-hidden bg-navy pb-20 pt-36 sm:pb-24 lg:pt-44" data-testid="blog-hero">
        <div className="bp-grid absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 sm:text-xs">
              <span className="inline-block h-2 w-2 bg-safety" /> Blog &amp; Technical Guides
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Field notes from five decades of jointing.
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Selection guides, technology comparisons and specification advice for
              engineers, EPCs and procurement teams.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="blog-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" delay={0.04}>
            {posts.map((post) => (
              <StaggerItem key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group flex h-full flex-col border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-safety hover:shadow-xl"
                >
                  <div className="relative overflow-hidden bg-navy">
                    <img src={post.image} alt={post.title} loading="lazy" className="aspect-[16/9] w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute left-3 top-3 bg-safety px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-white">
                      {post.tag || "Article"}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">
                      <span className="flex items-center gap-1.5"><CalendarDays size={12} className="text-safety" /> {formatDate(post.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock3 size={12} className="text-safety" /> {post.readTime}</span>
                    </div>
                    <h2 className="mt-3 font-display text-lg font-bold leading-snug tracking-tight text-navy">{post.title}</h2>
                    <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{post.excerpt}</p>
                    <span className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-navy transition-colors group-hover:text-safety">
                      Read article <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
};

export default Blog;
